export const DocumentStatus = {
  ANALYZING: 'analyzing',
  DONE: 'done',
  DRAFT: 'draft',
  EMBEDDING: 'embedding',
  ERROR: 'error',
  PARSING: 'parsing',
  UPLOADED: 'uploaded',
  WAITING_FOR_UPLOAD: 'waiting_for_upload',
} as const

export const FileUploadStatus = {
  PENDING: 'pending',
  UPLOADED: 'uploaded',
  FAILED: 'failed',
} as const

export const FILE_UPLOAD_STATUS_VALUES = Object.values(FileUploadStatus)

export const PreCheckStatus = {
  PENDING: 'pending',
  PASSED: 'passed',
  WARNING: 'warning',
} as const

export const PRE_CHECK_STATUS_VALUES = Object.values(PreCheckStatus)

export const PreCheckWarningCode = {
  MISSING_PERSON: 'missing_person',
  MISSING_PAGES: 'missing_pages',
} as const

export const PRE_CHECK_WARNING_CODES = Object.values(PreCheckWarningCode)

export type PreCheckWarningCodeType = (typeof PRE_CHECK_WARNING_CODES)[number]

export const Category = {
  COMMUNICATION_LANGUAGE: 'communication_language', // Kommunikation & språk
  EMOTIONS_BEHAVIORS: 'emotions_behaviors', // Känslor & beteenden
  HEALTH_CARE: 'health_care', // Hälsa & vård
  HOME: 'home', // Hemma
  LEISURE_SOCIAL: 'leisure_social', // Fritid & socialt
  MOTOR_FUNCTION: 'motor_function', // Motorik & fysisk funktion
  SCHOOL_WORK_DAILY: 'school_work_daily', // Skola, arbete & daglig verksamhet
  SUPPORT_AUTHORITIES: 'support_authorities', // Stöd & myndigheter
} as const

export const CATEGORY_VALUES = Object.values(Category)

// Activity Reference values and enum

// Activity Type values and enum
export const ActivityType = {
  APPLICATION: 'application',
  BIRTH: 'birth',
  BIRTHDAY: 'birthday',
  CERTIFICATE: 'certificate',
  DECISION: 'decision',
  DIAGNOSIS: 'diagnosis',
  DOCUMENT: 'document',
  LIFE_EVENT: 'life-event',
  PRESCRIPTION: 'prescription',
  REPORT: 'report',
  UNKNOWN: 'unknown',
} as const

export const ACTIVITY_TYPE_VALUES = Object.values(ActivityType)

export const ActivityReference = {
  APPLICATION: ActivityType.APPLICATION,
  CERTIFICATE: ActivityType.CERTIFICATE,
  DECISION: ActivityType.DECISION,
  DIAGNOSIS: ActivityType.DIAGNOSIS,
  DOCUMENT: ActivityType.DOCUMENT,
  PRESCRIPTION: ActivityType.PRESCRIPTION,
  REPORT: ActivityType.REPORT,
  UNKNOWN: ActivityType.UNKNOWN,
} as const

export const ACTIVITY_REFERENCE_VALUES = Object.values(ActivityReference)

export const IssuerRole = {
  CASE_MANAGER: 'case_manager',
  LEGAL_PROFESSIONAL: 'legal_professional',
  PHYSICIAN: 'physician',
  PSYCHOLOGIST: 'psychologist',
  THERAPIST: 'therapist',
} as const

export const ISSUER_ROLE_VALUES = Object.values(IssuerRole)

export type CategoryType = (typeof CATEGORY_VALUES)[number]

export type ActivityReferenceType = (typeof ACTIVITY_REFERENCE_VALUES)[number]

export type ActivityTypeType = (typeof ACTIVITY_TYPE_VALUES)[number]

export type IssuerRoleType = (typeof ISSUER_ROLE_VALUES)[number]

// Contact roles are a superset of IssuerRole: every issuer found on a Document maps
// 1:1 into a contact role, plus the roles that only ever show up as a mentioned
// person (assistants, teachers, relatives) rather than as a document issuer.
export const ContactRole = {
  CASE_MANAGER: IssuerRole.CASE_MANAGER,
  LEGAL_PROFESSIONAL: IssuerRole.LEGAL_PROFESSIONAL,
  PHYSICIAN: IssuerRole.PHYSICIAN,
  // Kept distinct from PHYSICIAN: "specialistläkare" is how Swedish medical documents
  // mark a consultant-level doctor, and collapsing it into the general physician role
  // loses information users recognise and search for.
  SPECIALIST_PHYSICIAN: 'specialist_physician',
  PSYCHOLOGIST: IssuerRole.PSYCHOLOGIST,
  THERAPIST: IssuerRole.THERAPIST,
  NURSE: 'nurse',
  SPECIAL_EDUCATOR: 'special_educator',
  PERSONAL_ASSISTANT: 'personal_assistant',
  COORDINATOR: 'coordinator',
  ADMINISTRATOR: 'administrator',
  RELATIVE: 'relative',
  OTHER: 'other',
} as const

export const CONTACT_ROLE_VALUES = Object.values(ContactRole)

export type ContactRoleType = (typeof CONTACT_ROLE_VALUES)[number]

export const ContactOrigin = {
  AI: 'ai',
  MANUAL: 'manual',
} as const

export const CONTACT_ORIGIN_VALUES = Object.values(ContactOrigin)

export type ContactOriginType = (typeof CONTACT_ORIGIN_VALUES)[number]

// ─── Billing ──────────────────────────────────────────────────────────────────

/**
 * Where an entitlement's money comes from. Deliberately not the same axis as
 * `BillingProvider`: a B2B licence and a manual grant are both `provider: 'manual'`
 * as far as payment handling goes, but they are funded very differently.
 */
export const BillingSource = {
  /** Paid through a payment provider by the individual's owner. */
  DIRECT: 'direct',
  /** A seat on an organisation's licence agreement. */
  LICENSE: 'license',
  /** Granted by hand in the admin — pilots, support cases, the pre-payment era. */
  MANUAL: 'manual',
} as const

export const BILLING_SOURCE_VALUES = Object.values(BillingSource)

/**
 * Who processes the money. `manual` means invoiced outside any provider, which is how
 * every B2B agreement is handled.
 */
export const BillingProvider = {
  MANUAL: 'manual',
  STRIPE: 'stripe',
} as const

export const BILLING_PROVIDER_VALUES = Object.values(BillingProvider)

/**
 * Billing period length. Independent of whether the purchase renews — a year can be
 * bought once or subscribed to.
 */
export const BillingInterval = {
  MONTH: 'month',
  YEAR: 'year',
} as const

export const BILLING_INTERVAL_VALUES = Object.values(BillingInterval)

/**
 * Lifecycle of a paid pool.
 *
 * `past_due` is not the same as expired: a failed renewal keeps its seats until the
 * period it was already paid for runs out, which is what `currentPeriodEnd` decides.
 */
export const BillingStatus = {
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  EXPIRED: 'expired',
} as const

export const BILLING_STATUS_VALUES = Object.values(BillingStatus)

/** Outcome of a single charge. */
export const PaymentStatus = {
  PENDING: 'pending',
  PAID: 'paid',
  FAILED: 'failed',
  REFUNDED: 'refunded',
} as const

export const PAYMENT_STATUS_VALUES = Object.values(PaymentStatus)
